/*
Copyright© Celligence International, LLC - 2024
This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { Alert, Button, Snackbar } from '@mui/material';
import EnvelopeWithBox from '../EnvelopeWithBox';
import Styles from '../../css/GoGreenPage.module.css';
import { useGoGreenContext, Action, Status } from '../../contexts/GoGreenContext';
import DefaultModal from '../modals/DefaultModal';
import { convertToPST, isStrEmptyOrNull } from '../../common/UtilityFunctions';
import { useCallback, useState } from 'react';
import esignDocTrackingListTable from '../tables/ESignDocTrackingListTable';
import LoadingModal from '../../components/modals/LoadingModal';

const ServicerGoGreen = () => {
  const {
    borrowerData,
    borrowerDataArr,
    status,
    borrowerNumber,
    actionInProcess,
    startESignProcess,
    modalButtons,
    sentEmailsToBorrowersRecord,
    esignDocTrackingList,
    isFetchingESignDocTrackingList,
    loadingApplicationList,
  } = useGoGreenContext();

  const getStatusModalContentText = () => {
    let statusText = <></>;
    const borrowerInfoText =
      borrowerData[borrowerNumber].fullName + ' with email address ' + borrowerData[borrowerNumber].emailAddress;
    if (status === Status.CONFIRMING && actionInProcess === Action.OPTOUT) {
      statusText = <>{'Opt borrower ' + borrowerInfoText + ' out?'}</>;
    } else if (status === Status.CONFIRMING && actionInProcess === Action.SENDEMAIL) {
      statusText = (
        <>
          <p>{'Send Go Green email to ' + borrowerInfoText + '?'}</p>
          <br></br>
          <p>
            Note: If the borrower is already logged in, they can access the Go Green page under the Loan Actions tab.
          </p>
        </>
      );
    } else if (status === Status.SUCCESS && actionInProcess === Action.SENDEMAIL) {
      statusText = <p>{'Successfully sent Go Green email to ' + borrowerInfoText}</p>;
    } else if (status === Status.SUCCESS && actionInProcess === Action.OPTOUT) {
      statusText = <p>{'Successfully opted out ' + borrowerInfoText}</p>;
    } else if (status === Status.ERROR && actionInProcess === Action.OPTOUT) {
      statusText = <p>{'Error opting out ' + borrowerInfoText}</p>;
    } else if (status === Status.ERROR && actionInProcess === Action.SENDEMAIL) {
      statusText = <p>{'Error sending Go Green email ' + borrowerInfoText}</p>;
    }
    return statusText;
  };

  const statusModal = () => {
    return (
      (status === Status.CONFIRMING || status === Status.SUCCESS || status === Status.ERROR) && (
        <DefaultModal
          className={Styles.modalcontainer}
          open={true}
          title={<div className={Styles.modaltitle}>{status}</div>}
          content={<div className={Styles.modalcontent}> {getStatusModalContentText()}</div>}
          footer={<div className={Styles.buttonrow}>{modalButtons()}</div>}
        ></DefaultModal>
      )
    );
  };

  const isDisabledForBorrower = useCallback(
    (borrower: BorrowerData) => {
      if (isStrEmptyOrNull(borrower.emailAddress)) return true;
      if (borrower.goGreen) {
        return false;
      } else {
        if (sentEmailsToBorrowersRecord[borrower.borrowerNumber]) return true;
        return false;
      }
    },
    [sentEmailsToBorrowersRecord],
  );

  const borrowerRows = () => {
    return borrowerDataArr
      ?.filter((borrower) => borrower?.borrowerType !== 'R')
      .map((borrower, index) => (
        <tr key={index}>
          <td>{isStrEmptyOrNull(borrower.fullName) ? '' : borrower.fullName}</td>
          <td>{isStrEmptyOrNull(borrower.emailAddress) ? '' : borrower.emailAddress}</td>
          <td>{borrower.goGreen ? 'Yes' : 'No'}</td>
          <td>{borrower.EConsentReceiveDate ? convertToPST(borrower.EConsentReceiveDate) : ''}</td>
          <td>
            <Button
              className={Styles.button}
              disabled={isDisabledForBorrower(borrower)}
              onClick={() => {
                startESignProcess(borrower.borrowerNumber);
              }}
            >
              {borrower.goGreen ? 'Opt Out' : 'Send Email'}
            </Button>
          </td>
          <td>{sentEmailsToBorrowersRecord[borrower.borrowerNumber] ? 'Yes' : 'No'}</td>
        </tr>
      ));
  };

  const tableHeader = () => {
    return (
      <tr>
        <th scope='col'>Borrower Name</th>
        <th scope='col'>Borrower Email</th>
        <th scope='col'>Go Green Status</th>
        <th scope='col'>Go Green Date (PT)</th>
        <th scope='col'>Action</th>
        <th scope='col'>Email sent</th>
      </tr>
    );
  };

  return (
    <>
      <EnvelopeWithBox blockName={'Go Green'}>
        {
          <table className={Styles.goGreenTable}>
            <thead>{tableHeader()}</thead>
            <tbody>{borrowerRows()}</tbody>
            <>{statusModal()}</>
          </table>
        }
        <></>
      </EnvelopeWithBox>
      <EnvelopeWithBox blockName={'Go Green Email History'}>
        {esignDocTrackingListTable(esignDocTrackingList, 'GO GREEN')}
        <></>
      </EnvelopeWithBox>
      {(status === Status.LOADING || isFetchingESignDocTrackingList || loadingApplicationList) && (
        <LoadingModal open={true} />
      )}
    </>
  );
};

export default ServicerGoGreen;
